import { defaultEntityState } from '@core/state/entities/entity';
import {
  mergeEntityState,
  removeEntities,
  removeEntitiesByFilter,
} from '@core/state/entities/entity.helpers';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { authApiActions } from '@core/auth/auth.actions';
import { IProjectDetails } from './project-details.entity';
import { createReducer, on } from '@ngrx/store';
import { entityKey } from '@core/state/entities/entity-name-map';
import {
  childIdsToProject,
  childIdsToProjectFromNormalizedPayload,
} from '@core/state/entities/project/project.reducer';
import { IEntityState } from '@core/state/entities/entity';
import {
  fenceDeletedProject,
  isProjectJobFamilyAuthorized,
  isStaleProjectJobSnapshot,
  NormalizedProjectJobSnapshotPayload,
  ProjectDetailsSnapshotView,
  ProjectJobFamily,
  projectJobFamilies,
  readProjectJobPayloadProjectId,
  readProjectSnapshotVersion,
  resetProjectJobSnapshotVersions,
} from '@core/state/entities/project-job-snapshot';

interface ProjectDetailsState extends IEntityState<IProjectDetails> {
  /**
   * Mirrors the job entity reducers' whole-Project membership ordering token. There is one audit
   * version per Project, so all job families share this single map rather than tracking one each.
   */
  projectJobSnapshotVersions?: Readonly<Record<string, number>>;
  /** Client-side terminal fence: a deleted Project id can never be resurrected by late payloads. */
  deletedProjectIds?: Readonly<Record<string, true>>;
}

/** Keeps the existing membership ids for one family while accepting the rest of the payload. */
function preserveFamilyMembership<T extends ProjectDetailsSnapshotView>(
  incoming: T,
  existing: IProjectDetails,
  family: ProjectJobFamily
): T {
  return {
    ...incoming,
    [family.membershipField]: (existing as unknown as Record<string, unknown>)[
      family.membershipField
    ],
  } as T;
}

/**
 * Keeps both the existing membership ids and the existing view-permission answers for one family.
 * Used when the payload cannot prove it is newer than the last accepted snapshot, so neither its
 * membership nor its grant may be believed for that family.
 */
function preserveFamilyEvidence<T extends ProjectDetailsSnapshotView>(
  incoming: T,
  existing: IProjectDetails,
  family: ProjectJobFamily
): T {
  const preserved = preserveFamilyMembership(incoming, existing, family);
  return {
    ...preserved,
    permissionReport: {
      ...(incoming.permissionReport ?? {}),
      project: {
        ...(incoming.permissionReport?.project ?? {}),
        ...Object.fromEntries(
          family.viewPermissions.map((permission) => [
            permission,
            existing.permissionReport?.project?.[permission],
          ])
        ),
      },
    },
  } as T;
}

function mergeProjectDetails(
  state: ProjectDetailsState,
  props: NormalizedProjectJobSnapshotPayload,
  projectId?: string,
  details?: ProjectDetailsSnapshotView
): ProjectDetailsState {
  const normalizedProps =
    projectId && details
      ? {
          ...props,
          entities: {
            ...props.entities,
            projectDetails: {
              ...props.entities.projectDetails,
              [projectId]: details,
            },
          },
        }
      : props;
  return mergeEntityState(
    entityKey.projectDetails,
    state,
    normalizedProps as never
  ) as ProjectDetailsState;
}

/**
 * The per-family job id arrays live on ProjectDetails, and every normalized project payload
 * overwrites them wholesale. They therefore need the same ordering discipline as the job entity
 * reducers: the enclosing Project's `audit.version` decides whether this payload's membership and
 * view-permission answers may be believed, per family.
 */
function mergeProjectDetailsWithJobSnapshots(
  state: ProjectDetailsState,
  props: NormalizedProjectJobSnapshotPayload
): ProjectDetailsState {
  const projectId = readProjectJobPayloadProjectId(props);
  if (projectId && state.deletedProjectIds?.[projectId]) {
    return state;
  }

  const incomingDetails = projectId
    ? props.entities.projectDetails?.[projectId]
    : undefined;
  const existingDetails = projectId ? state.byId[projectId] : undefined;
  const knownVersion = projectId
    ? state.projectJobSnapshotVersions?.[projectId]
    : undefined;
  const projectVersion =
    projectId !== null ? readProjectSnapshotVersion(props, projectId) : null;

  if (!projectId || !incomingDetails || projectVersion === null) {
    // No Project row, so nothing orders membership. An unversioned payload may never restore a
    // grant or resurrect ids an accepted snapshot pruned, but a denial is not a membership claim:
    // it says this principal may not see the family at all, so it clears that family's ids.
    if (!projectId || !incomingDetails) {
      return mergeProjectDetails(state, props);
    }
    let details: ProjectDetailsSnapshotView = incomingDetails;
    for (const family of projectJobFamilies) {
      if (
        !isProjectJobFamilyAuthorized(
          family,
          incomingDetails.permissionReport?.project
        )
      ) {
        details = { ...details, [family.membershipField]: [] };
      } else if (knownVersion !== undefined && existingDetails) {
        details = preserveFamilyEvidence(details, existingDetails, family);
      }
    }
    return details === incomingDetails
      ? mergeProjectDetails(state, props)
      : mergeProjectDetails(state, props, projectId, details);
  }

  const staleByVersion =
    knownVersion !== undefined && projectVersion < knownVersion;
  let details: ProjectDetailsSnapshotView = incomingDetails;
  for (const family of projectJobFamilies) {
    const authorized = isProjectJobFamilyAuthorized(
      family,
      incomingDetails.permissionReport?.project
    );
    if (isStaleProjectJobSnapshot(knownVersion, projectVersion, authorized)) {
      if (existingDetails) {
        details = preserveFamilyEvidence(details, existingDetails, family);
      }
    } else if (!authorized) {
      // The server sends an empty collection when it denies the family; mirror that rather than
      // trusting ids an inconsistent payload may still carry.
      details = { ...details, [family.membershipField]: [] };
    }
  }

  const merged =
    details === incomingDetails
      ? mergeProjectDetails(state, props)
      : mergeProjectDetails(state, props, projectId, details);
  if (staleByVersion || knownVersion === projectVersion) {
    return merged;
  }
  return {
    ...merged,
    projectJobSnapshotVersions: {
      ...state.projectJobSnapshotVersions,
      [projectId]: projectVersion,
    },
  };
}

export const projectDetailsEntityReducer = createReducer(
  defaultEntityState<IProjectDetails>(),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.joinRequestsResponseSent,
    projectDetailActions.projectUpdated,
    projectDetailActions.projectListingUpdated,
    projectDetailActions.registered,
    projectDetailActions.agreementModeUpdated,

    (state, props) => mergeProjectDetailsWithJobSnapshots(state, props)
  ),
  on(projectDetailActions.annotationQuestionRepositioned, (state, props) =>
    childIdsToProject(
      entityKey.annotationQuestions,
      state,
      props.annotationQuestions
    )
  ),
  on(
    projectDetailActions.annotationQuestionAddedSuccessfully,
    projectDetailActions.customAnnotationQuestionCopiedSuccessfully,
    projectDetailActions.customAnnotationQuestionSavedSuccessfully,
    (state, props) =>
      childIdsToProjectFromNormalizedPayload(
        entityKey.annotationQuestions,
        state,
        props
      )
  ),
  on(projectDetailActions.annotationQuestionDeleted, (state, props) =>
    childIdsToProjectFromNormalizedPayload(
      entityKey.annotationQuestions,
      state,
      props,
      true
    )
  ),
  on(projectDetailActions.stageAdded, (state, props) =>
    childIdsToProjectFromNormalizedPayload(entityKey.stages, state, props)
  ),
  on(projectDetailActions.searchImportJobStarted, (state, props) =>
    childIdsToProjectFromNormalizedPayload(
      entityKey.searchImportJobs,
      state,
      props
    )
  ),
  on(projectDetailActions.searchRemoved, (state, props) =>
    childIdsToProjectFromNormalizedPayload(
      entityKey.searches,
      state,
      props,
      true
    )
  ),
  on(projectDetailActions.searchImportJobDeleted, (state, props) =>
    childIdsToProjectFromNormalizedPayload(
      entityKey.searchImportJobs,
      state,
      props,
      true
    )
  ),
  on(
    authApiActions.startUserImpersonation,
    authApiActions.stopUserImpersonation,
    (state): IEntityState<IProjectDetails> =>
      // Project audit versions are only comparable within one effective principal, so the next
      // payload from the new principal must be authoritative even at an unchanged version. The
      // job entity reducers reset their own maps the same way.
      resetProjectJobSnapshotVersions(state as ProjectDetailsState)
  ),
  on(projectDetailActions.projectDeleted, (state, props) => {
    const projectDetailsState = state as ProjectDetailsState;
    return fenceDeletedProject(
      projectDetailsState,
      removeEntitiesByFilter(
        projectDetailsState,
        (projChild) => projChild.projectId === props.projectId
      ) as ProjectDetailsState,
      props.projectId,
      props.terminalPhysicalDeletion
    );
  })
);
